import React from 'react';
import { Sun, BookOpen, FlaskConical, AlertTriangle, MessageSquare, Info } from 'lucide-react';
import SaveToIdeasButton from '../components/SaveToIdeasButton';

const VitaminDEducationPage = () => {
  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-xl">
              <Sun className="h-8 w-8 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-amber-600 mb-1">Integrative Health → Vitamins</p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
                Vitamin D and CLL (Educational)
              </h1>
            </div>
          </div>
          <SaveToIdeasButton
            title="Vitamin D and CLL"
            category="Vitamins"
            sourcePage="Vitamin D and CLL (Educational)"
            sourceUrl="/vitamin-d-education"
            variant="compact"
          />
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        
        {/* Section 1: What is Vitamin D */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
            What is Vitamin D?
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p className="mb-3">
              Vitamin D is a fat-soluble vitamin that plays an important role in many systems in the body, including:
            </p>
            <ul className="space-y-2 ml-1">
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Bone and muscle health</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Immune system regulation</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Inflammatory processes</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>General metabolic function</span>
              </li>
            </ul>
            <p className="mt-4">
              Low Vitamin D levels are common worldwide and can occur in people with or without Chronic Lymphocytic Leukaemia (CLL).
            </p>
          </div>
        </div>

        {/* Section 2: Why Vitamin D is often discussed */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-amber-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-100 rounded-lg flex-shrink-0 mt-1">
              <BookOpen className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Why Vitamin D is often discussed in CLL
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">Vitamin D is frequently mentioned in CLL education and patient communities because:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Deficiency is common</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Levels are easy to measure with a blood test</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Vitamin D is involved in immune system function</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Research has explored associations between Vitamin D levels and general health outcomes</span>
                  </li>
                </ul>
                <div className="mt-4 bg-white border border-amber-200 rounded-xl p-4">
                  <p className="font-medium text-slate-800">
                    This does not mean Vitamin D treats, prevents, or cures CLL.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: What research suggests */}
        <div className="p-6 md:p-8 border-b border-slate-100">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0 mt-1">
              <FlaskConical className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                What research generally suggests
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">Current research suggests that:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Many people with CLL have low Vitamin D levels</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Correcting deficiency may support overall health</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Supplementation is usually intended to restore adequate levels, not to treat CLL itself</span>
                  </li>
                </ul>
                <p className="mt-4 text-slate-600 italic">
                  Research in this area focuses on associations rather than cause-and-effect relationships.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Important considerations */}
        <div className="p-6 md:p-8 border-b border-slate-100 bg-red-50/50">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-red-100 rounded-lg flex-shrink-0 mt-1">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                Important considerations
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Vitamin D is fat-soluble, which means it can accumulate in the body if taken in excessive amounts.
                </p>
                <p className="mb-3 font-medium text-slate-800">Important points to consider:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Dosage should be guided by blood test results</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Very high doses may cause harm</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Vitamin D may interact with certain medications</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Individual needs can vary significantly</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Section 5: What to discuss */}
        <div className="p-6 md:p-8 bg-green-50/70">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-green-100 rounded-lg flex-shrink-0 mt-1">
              <MessageSquare className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
                What to discuss with your healthcare team
              </h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p className="mb-3">You may wish to ask your healthcare provider:</p>
                <ul className="space-y-2 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Should my Vitamin D level be tested?</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>What range is appropriate for me?</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>Is supplementation recommended in my situation?</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>How often should my levels be monitored?</span>
                  </li>
                </ul>
                <p className="mt-4 font-medium text-slate-800">
                  This platform allows you to track Vitamin D levels alongside other blood results over time, helping you observe patterns and prepare informed questions for medical appointments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Educational Note Footer */}
      <div className="bg-slate-100 rounded-xl p-5 border border-slate-200">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-slate-700 mb-1">Educational note</p>
            <p className="text-sm text-slate-600 leading-relaxed">
              This information is provided for educational purposes only and does not replace medical advice. Always discuss Vitamin D use with a qualified healthcare professional.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Save Button */}
      <div className="flex justify-center pt-4">
        <SaveToIdeasButton
          title="Vitamin D and CLL"
          category="Vitamins"
          sourcePage="Vitamin D and CLL (Educational)"
          sourceUrl="/vitamin-d-education"
        />
      </div>
    </div>
  );
};

export default VitaminDEducationPage;
